/**
 * English translations for Radar component
 * Phase 4.3: Internationalization
 */

export const radarI18nEn = {
  // Component labels
  title: 'Requests',
  daysSuffix: 'days',
  daysShort: 'd',
  nowLabel: 'NOW',

  // Zone labels
  zones: {
    panic: 'Panic Zone',
    middle: 'Middle Zone',
    outer: 'Outer Zone',
  },

  // Status labels
  status: {
    open: 'Open',
    accepted: 'Accepted',
    declined: 'Declined',
    filled: 'Filled',
    booked: 'Booked',
    pending: 'Pending',
  },

  // Bubble type labels
  bubbleTypes: {
    outgoing: 'from me',
    incoming: 'to me',
  },

  // Button labels
  buttons: {
    refresh: 'Refresh',
    retry: 'Try again',
    reload: 'Reload page',
  },

  // Form labels
  form: {
    timeRange: 'Time Range',
  },

  // Loading states
  loading: {
    default: 'Loading radar...',
    refreshing: 'Refreshing radar...',
  },

  // Empty states
  empty: {
    default: 'No substitution requests in selected time range',
    noResults: 'No requests found',
  },

  // Error messages
  errors: {
    title: 'Radar could not be loaded',
    description: 'An error occurred. Please try again.',
    unknown: 'Unknown error',
  },

  // Tooltip content
  tooltip: {
    subject: 'Subject',
    date: 'Date',
    time: 'Time',
    room: 'Room',
    class: 'Class',
    status: 'Status',
    teacher: 'Teacher',
    // frost-v2 (E11/E13): frost chip replaces status chip + gate line
    gateChip: '❄ Awaiting director approval',
    gateLine: (date: string) => `Distribution paused · deadline frozen since ${date}`,
    gateLineNoDate: 'Distribution paused · deadline frozen',
  },

  // ARIA labels
  aria: {
    regionLabel: 'Substitution Requests Radar',
    canvasLabel: 'Interactive visualization of substitution requests',
    instructions: `This radar shows substitution requests by urgency and time.
Press Tab to navigate between requests.
Use arrow keys to move between requests.
Press Enter to view details.
Press Escape to clear selection.`,
    nowIndicator: 'Now',
    refreshButton: 'Refresh radar',
    timeRangeSlider: 'Select time range',
    // frost-v2 (A2): per-bubble gate hint for screen readers
    bubbleGate: 'Awaiting director approval',
  },

  // Legend
  legend: {
    outgoingRequests: 'Requests from me',
    incomingRequests: 'Requests to me',
    panicZone: 'Panic Zone',
    middleZone: 'Middle Zone',
    hoursShort: 'h',
    splitHint: 'A request may split into up to 3 bubbles: filled / declined / open.',
    // frost-v2 (E12): frost legend
    gateFrozen: '❄ Awaiting director approval',
  },

  // Cascade teachers in hover (yellow-fix-v2/v3)
  hover: {
    takenOver: 'Taken over by',
    declinedBy: 'Declined by',
    stillOpen: 'Still open — Contacted',
    more: 'more',
  },

  // Prep
  prep: {
    open: 'Prep open',
    status: {
      complete: 'Prep complete',
      partial: 'Prep partial',
      requested: 'Prep requested',
      noPrep: 'No Prep',
    },
    request: {
      button: 'Request Prep',
      title: 'Request Prep',
      reason: 'Reason (optional)',
      reasonPlaceholder: 'e.g. Prep incomplete',
      submit: 'Send',
      submitting: 'Requesting...',
      success: 'Prep request sent',
      error: 'Prep request failed',
      alreadyRequested: 'Prep already requested',
    },
    resolve: {
      button: 'Prep Request Fulfilled',
      loading: 'Marking...',
      success: 'Prep request marked as fulfilled',
      error: 'Action failed',
      reasonLabel: 'Reason',
    },
  },

  // Admin-specific labels
  admin: {
    title: 'Substitution Radar (Admin)',
    teachers: 'Teachers',
    requests: 'Requests',
    panic: 'PANIC',
    requestDetails: 'Request Details',
    contacted: 'Contacted',
    acceptance: 'Acceptance',
    pending: 'Pending',
    declined: 'Declined',
    substitution: 'Substitution',
    panicZoneUrgent: 'PANIC ZONE — Substitution urgent!',
    open: 'Open',
    filled: 'Filled',
    requestOpen: 'Request open',
    summaryOpen: 'Open',
    summaryFilled: 'Filled',
    summaryPanic: 'Panic',
    // bubblenew-v2: badge ring (collapse handle on the circumference)
    badgeCollapse: 'Collapse bundle',
    badgeExpand: 'Expand bundle',
    badgeLessonCount: (n: number) => `${n} ${n === 1 ? 'lesson' : 'lessons'}`,
    hoursUntil: {
      lessThanOne: 'in <1h',
      oneDay: 'in 1 day',
      oneDayWithHours: 'in 1 day',
      days: 'in days',
    },
    // frost-v2 (E15): gate banner in the admin slide-over
    gateBanner: '⏸ Awaiting director approval — distribution paused',
  },

  // Slide-over panel (Admin bubble detail)
  slideOver: {
    title: 'Substitution Request',
    lesson: 'Lesson',
    participants: 'Participants',
    responses: 'Responses',
    prep: 'Preparation',
    actions: 'Actions',
    date: 'Date',
    time: 'Time',
    subject: 'Subject',
    klass: 'Class',
    room: 'Room',
    lessons: 'Lessons',
    reason: 'Reason',
    notes: 'Notes',
    created: 'Created',
    requesting: 'Requesting',
    substitute: 'Substitute',
    notAssigned: 'Not yet assigned',
    panicZone: 'Panic Zone',
    middleZone: 'Middle Zone',
    outerZone: 'Outer Zone',
    noResponses: 'No responses yet',
    contacted: 'contacted',
    sendMessage: 'Send Message',
    sendReminder: 'Send Reminder',
    assign: 'Assign Substitute',
    cancelRequest: 'Cancel Request',
    cancelConfirm: 'Really cancel this request? All pending responses will be declined.',
    cancel: 'Cancel',
    confirm: 'Confirm',
    sending: 'Sending...',
    assigning: 'Assigning...',
    cancelling: 'Cancelling...',
    searchTeacher: 'Search teacher...',
    noTeachersFound: 'No teachers found',
    multiDay: 'Multiple Days',
    prepComplete: 'Prep complete',
    prepPartial: 'Prep partial',
    prepRequested: 'Prep requested',
    prepNoInfo: 'No prep info',
    requestedFrom: 'Requested from',
    moreResponses: 'more',
    lessResponses: 'show less',
    reminderPlaceholder: 'Optional message (or use default)...',
    overwriteConfirm: 'Overwrite',
    alreadyAssigned: 'Already assigned to',
    overwriteQuestion: 'Overwrite?',
    statusLabels: {
      pending: 'Pending',
      accepted: 'Accepted',
      declined: 'Declined',
      cancelled: 'Cancelled',
      booked: 'Booked',
      partially_filled: 'Partially Filled',
      filled: 'Filled',
      expired: 'Expired',
      // frost-v2: director gate statuses instead of raw-string fallback
      director_pending: 'Awaiting director approval',
      director_returned: 'Returned (adjusted)',
    },
    lessonStatusLabels: {
      pending: 'Open',
      assigned: 'Assigned',
      accepted: 'Accepted',
      declined: 'Declined',
      cancelled: 'Cancelled',
      booked: 'Booked',
      expired: 'Expired',
    },
    cancelErrors: {
      alreadyCancelled: 'This request has already been cancelled',
      alreadyBooked: 'This request is already booked and cannot be cancelled',
      alreadyDeclined: 'This request has already been declined',
      noPermission: 'No permission to cancel this request',
      notFound: 'Request not found',
      generic: 'Cancellation failed',
    },
  },

  // Filter drawer (filter-fix-v2)
  filter: {
    title: 'Filter',
    reset: 'Reset',
    close: 'Close',
    activeOnly: 'Active only (Open + Interested + Taken over)',
    activeOnlyHint: 'Status filter is overridden by "Active only"',
    sectionStatus: 'Status',
    sectionZone: 'Urgency',
    sectionType: 'Type',
    sectionSubject: 'Subject',
    sectionFaculty: 'Faculty',
    sectionClass: 'Class',
    sectionRequester: 'Requesting teacher',
    sectionSpecials: 'Special states',
    specialPrepOpen: 'Prep open',
    specialPrepRequested: 'Prep requested',
    specialStornoPending: 'Cancellation pending',
    specialDirectorAction: 'Director action needed',
    specialHasUnread: 'Unread message',
    specialIsSplit: 'Split bubble',
    specialPartial: 'Partially fulfilled',
    specialNeedsAdminAssignment: 'Manual assignment needed',
    // frost-v2 (E12): filter special "with director"
    specialDirectorGate: 'With director',
    lockedHint: 'Filtering is locked by the superadmin. You see the school-wide default.',
    badgeAria: (n: number) => `${n} active filter(s)`,
    emptyAll: 'All visible',
    emptyFiltered: 'No requests found',
    specialsOrHint: 'Several specials: a bubble matches if it has at least one.',
    statusPending: 'Open',
    statusInterested: 'Interested / waiting pool',
    statusFilled: 'Taken over',
    statusDeclined: 'Declined',
    statusCancelled: 'Cancelled',
    statusExpired: 'Expired',
    statusAssignedElsewhere: 'Taken over by someone else',
    zonePanic: 'Panic zone',
    zoneMiddle: 'Middle zone',
    zoneOuter: 'Outer zone',
    typeOutgoing: 'Requests from me',
    typeIncoming: 'Requests to me',
    visibleAll: (n: number) => `${n} requests visible`,
    visiblePartial: (visible: number, total: number) => `${visible} of ${total} requests visible`,
    checklistAll: 'All',
    checklistNoneRestriction: 'No restriction',
    checklistSearch: 'Search…',
    checklistNoHits: 'No matches',
    checklistAllVisible: 'All visible (not restricted)',
    countRequests: (visible: number, total: number) => `${visible}/${total} requests`,
  },
}

export default radarI18nEn
